// Popover positioning + click-outside hooks. Both portal the dropdown to
// document.body so it escapes the scrollable sidebar panel (otherwise the
// popover gets clipped). Ported from
// private/nos-housekeeping-features/project/components-standalone/type-selector.jsx.

import * as React from "react";

/** Close `onClose` when a mousedown lands outside both `ref` and the
 *  optional `extraRef` (used for portaled popovers).
 *
 *  Also treats clicks inside ANY element marked
 *  `data-popover-portal="1"` as "inside". This is what makes nested
 *  popovers work — e.g. a `TypeSelector` opened from within a
 *  `LocationTagSelector`'s configure form portals to `document.body`,
 *  so it's NOT a descendant of the outer popRef. Without this escape
 *  hatch, every nested popover click closes its ancestors. */
export function useClickOutside(
  ref: React.RefObject<HTMLElement | null>,
  onClose: () => void,
  extraRef?: React.RefObject<HTMLElement | null>,
): void {
  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const inMain = ref.current?.contains(target) ?? false;
      const inExtra = extraRef?.current?.contains(target) ?? false;
      const inAnyPopover =
        target instanceof Element &&
        target.closest('[data-popover-portal="1"]') !== null;
      if (!inMain && !inExtra && !inAnyPopover) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [ref, extraRef, onClose]);
}

export type PopoverPos = {
  left: number;
  top: number | "auto";
  bottom: number | "auto";
  width: number;
  maxHeight: number;
  flipUp: boolean;
};

type PopoverOpts = {
  gap?: number;
  estHeight?: number;
  minWidth?: number | null;
  align?: "start" | "end";
};

/** Compute viewport-anchored position for a popover rooted at `triggerRef`.
 *  Listens to scroll + resize so the popover tracks the trigger inside
 *  scrollable parents (e.g. the inventory sidebar). Flips above the
 *  trigger when there's no room below. */
export function usePopoverPosition(
  triggerRef: React.RefObject<HTMLElement | null>,
  open: boolean,
  opts: PopoverOpts = {},
): PopoverPos | null {
  const { gap = 6, estHeight = 360, minWidth = null, align = "start" } = opts;
  const [pos, setPos] = React.useState<PopoverPos | null>(null);

  React.useLayoutEffect(() => {
    if (!open || !triggerRef.current) {
      setPos(null);
      return;
    }
    const update = () => {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const spaceBelow = vh - r.bottom;
      const flipUp = spaceBelow < estHeight && r.top > spaceBelow;

      let width = Math.max(r.width, minWidth ?? 0);
      width = Math.min(width, vw - 16);

      let left = align === "end" ? r.right - width : r.left;
      if (left + width > vw - 8) left = vw - 8 - width;
      if (left < 8) left = 8;

      setPos({
        left,
        top: flipUp ? "auto" : r.bottom + gap,
        bottom: flipUp ? vh - r.top + gap : "auto",
        width,
        maxHeight: flipUp ? r.top - gap - 8 : spaceBelow - gap - 8,
        flipUp,
      });
    };

    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, triggerRef, gap, estHeight, minWidth, align]);

  return pos;
}
